import { useEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { Crosshair, BarChart3, Bell, Users } from 'lucide-react';

const timelineSteps = [
    { step: 1, icon: Crosshair, title: 'Detection', time: '0-3s', description: 'Sensors detect anomaly' },
    { step: 2, icon: BarChart3, title: 'Analysis', time: '3-5s', description: 'AI validates threat' },
    { step: 3, icon: Bell, title: 'Alert', time: '5-8s', description: 'Notifications sent' },
    { step: 4, icon: Users, title: 'Response', time: '8-15s', description: 'Teams mobilized' },
];

export function TimelineSection() {
    const { ref, isInView } = useScrollAnimation<HTMLElement>({ threshold: 0.3 });
    const reducedMotion = useReducedMotion();
    const hasAnimated = useRef(false);

    useEffect(() => {
        if (!isInView || hasAnimated.current || reducedMotion) return;
        hasAnimated.current = true;

        animate('.timeline-title', {
            translateY: [30, 0],
            opacity: [0, 1],
            duration: 800,
            ease: 'outExpo',
        });

        animate('.timeline-step', {
            translateY: [50, 0],
            opacity: [0, 1],
            duration: 600,
            ease: 'outExpo',
            delay: stagger(150, { start: 200 }),
        });

        animate('.timeline-connector', {
            scaleX: [0, 1],
            duration: 400,
            ease: 'outExpo',
            delay: stagger(150, { start: 400 }),
        });
    }, [isInView, reducedMotion]);

    return (
        <section
            ref={ref}
            className="py-24 px-4 md:px-8 lg:px-16"
            style={{ background: 'var(--light-bg)' }}
            aria-labelledby="timeline-title"
        >
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-16">
                    <h2
                        id="timeline-title"
                        className={`timeline-title text-4xl md:text-5xl font-display font-bold text-white mb-4 ${reducedMotion ? '' : 'opacity-0'}`}
                    >
                        Response in{' '}
                        <span className="text-[var(--fire-orange)]">Under 15 Seconds</span>
                    </h2>
                    <p className={`timeline-title text-gray-400 text-lg max-w-2xl mx-auto ${reducedMotion ? '' : 'opacity-0'}`}>
                        From detection to coordinated response - our system acts faster than any human could.
                    </p>
                </div>

                {/* Desktop Timeline */}
                <div className="hidden md:flex items-start justify-between">
                    {timelineSteps.map((step, index) => (
                        <div key={step.step} className="relative flex-1">
                            <div className={`timeline-step text-center ${reducedMotion ? '' : 'opacity-0'}`}>
                                <div
                                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                                    style={{
                                        background: 'var(--gradient-fire)',
                                        boxShadow: '0 0 20px rgba(255, 107, 53, 0.4)'
                                    }}
                                >
                                    <step.icon className="w-7 h-7 text-white" aria-hidden="true" />
                                </div>

                                <div
                                    className="inline-block px-3 py-1 rounded-full text-xs font-mono font-medium mb-2"
                                    style={{ background: 'rgba(255, 107, 53, 0.2)', color: 'var(--fire-orange)' }}
                                >
                                    {step.time}
                                </div>

                                <h3 className="text-xl font-display font-bold text-white mb-1">
                                    {step.title}
                                </h3>
                                <p className="text-sm text-gray-400">{step.description}</p>
                            </div>

                            {index < timelineSteps.length - 1 && (
                                <div
                                    className={`timeline-connector absolute top-8 left-[60%] right-0 h-0.5 ${reducedMotion ? '' : 'scale-x-0'}`}
                                    style={{
                                        background: 'linear-gradient(90deg, var(--fire-orange), var(--fire-yellow))',
                                        transformOrigin: 'left'
                                    }}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {/* Mobile Timeline */}
                <div className="md:hidden space-y-8">
                    {timelineSteps.map((step, index) => (
                        <div
                            key={step.step}
                            className={`timeline-step flex items-start gap-4 ${reducedMotion ? '' : 'opacity-0'}`}
                        >
                            <div className="flex flex-col items-center">
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{ background: 'var(--gradient-fire)' }}
                                >
                                    <step.icon className="w-5 h-5 text-white" aria-hidden="true" />
                                </div>
                                {index < timelineSteps.length - 1 && (
                                    <div
                                        className="w-0.5 h-16 mt-2"
                                        style={{ background: 'var(--fire-orange)' }}
                                    />
                                )}
                            </div>

                            <div className="flex-1 pt-1">
                                <div
                                    className="inline-block px-2 py-0.5 rounded-full text-xs font-mono font-medium mb-1"
                                    style={{ background: 'rgba(255, 107, 53, 0.2)', color: 'var(--fire-orange)' }}
                                >
                                    {step.time}
                                </div>
                                <h3 className="text-lg font-display font-bold text-white">
                                    {step.title}
                                </h3>
                                <p className="text-sm text-gray-400">{step.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default TimelineSection;
